import {
  DEVICE_REVOKED,
  DEVICE_REVOKED_CLOSE,
  FURNISH_RATES,
  GRAB_REFUSALS,
  isMaterial,
  mediaInfoOf,
  modelCatalogueOf,
  panoVariantsOf,
  phaseFrom,
  roundPose,
  servedWorldsOf,
  tuningOf,
  type CueMap,
  type FurnitureAssigned,
  type FurnitureWire,
  type GrabRefusal,
  type Lesson,
  type MediaInfo,
  type ModelCatalogue,
  type MoveWire,
  type MovesWire,
  type PanoVariants,
  type PieceInfo,
  type PieceWire,
  type PosePayload,
  type PrefetchReport,
  type PrefetchTuning,
  type ServedWorld,
  type SessionPhase,
} from '@vrlearning/lesson-schema';
import { deviceFetch, type DeviceIdentity } from './pairing-client.js';
import type { LessonResult } from '../engine/player.js';
import { cuesFrom, guideOf, type Guide } from '../engine/guide.js';
import type { GrabAnswer } from '../engine/furnishing.js';

/**
 * The headset's end of the live session (docs/05-session-protocol.md).
 *
 * The API owns the state; this listens and renders what it is told (ADR-007).
 * It never chooses a session: the teacher pushes one, and `session.assigned`
 * arrives whether the headset was waiting on the lobby screen or in the middle
 * of the last lesson.
 *
 * The socket is injected so the reconnect behaviour can be tested without one.
 * Reconnecting is not an edge case here -- classroom wifi drops constantly,
 * and a ticket is good exactly once, so every attempt fetches a new one.
 */

export type Socket = {
  send(data: string): void;
  close(): void;
};

export type SocketHandlers = {
  onOpen(): void;
  onMessage(raw: string): void;
  /** `code`: the close code, when the socket was closed with one (DEVICE_REVOKED_CLOSE). */
  onClose(code?: number): void;
};

export type SessionClientOptions = {
  /**
   * Fetches a single-use ticket. Rejects when the API cannot be reached, and with an error whose
   * `revoked` is true (pairing-client.ts's DeviceRevokedError) when the headset was unlinked.
   */
  ticket: () => Promise<string>;
  open: (url: string, handlers: SocketHandlers) => Socket;
  appVersion?: string;
  /** How long to wait before each reconnect; the last value repeats. */
  retryDelaysMs?: number[];
  /** How long to wait for a result to be acknowledged before giving up. */
  ackTimeoutMs?: number;
};

export type Assignment = {
  sessionId: string;
  lesson: Lesson;
  media: Record<string, string>;
  /** Media id -> subtitle cues for its speech (ADR-035); `{}` from an API without them. */
  cues: CueMap;
  /**
   * The meubelset of a lesson with a furnish step (ADR-037): its pieces, whose signed URLs are in
   * `media`. Absent for every other lesson.
   */
  furniture?: FurnitureAssigned;
  /** Size and hash per signed id, to check a prefetched file against (ADR-038); `{}` from an older API. */
  mediaInfo: MediaInfo;
  /** The prefetch tunables, clamped (tuningOf); the defaults from an older API. */
  tuning: PrefetchTuning;
  /**
   * The API sent `prefetch` tunables: it knows ADR-038. An older one would answer every
   * `device.prefetch` and `device.mediaRefresh` with `unknown-type`, so nothing runs behind its lesson.
   */
  prefetchOffered: boolean;
  /**
   * The 360° photos' signed cubes and light copies (ADR-041), beside `media`; `{}` from an older
   * API, and an entry it cannot trust is dropped (that photo is shown from its JPEG).
   */
  variants: PanoVariants;
  /**
   * The library models the lesson names (ADR-043, contract C4): slug -> facts, their GLBs and
   * sound takes signed in `media`; `{}` from an older API or for junk (modelCatalogueOf).
   */
  models?: ModelCatalogue;
  /**
   * The served world packages of the lesson's world (ADR-049, contract W8): slug -> package, its
   * files' signed URLs in `media`; `{}` from an older API, and an entry of the wrong shape is dropped.
   */
  worlds: Readonly<Record<string, ServedWorld>>;
};

/**
 * `session.prepare` (ADR-038): the lesson a teacher is setting ready, as `session.assigned` will
 * carry it at the start. The headset fills its cache with it and stays in the lobby.
 */
export type Preparation = Assignment;

/**
 * `preview.open` (ADR-058): a world or object to open in Bekijken mode, or -- a platform test headset,
 * from Steekproef -- a school's lesson to play solo, by a ticket for this headset.
 */
export type PreviewOpen = {
  readonly ticket: string;
  readonly kind: 'world' | 'object' | 'lesson';
  readonly title: string;
};

const PREVIEW_TICKET = /^[A-Za-z0-9_-]{43}$/;

/** A `preview.open` frame's top-level fields, or undefined for anything that is not exactly one. */
export function previewOpenOf(frame: Record<string, unknown>): PreviewOpen | undefined {
  const { ticket, kind, title } = frame;
  if (typeof ticket !== 'string' || !PREVIEW_TICKET.test(ticket)) return undefined;
  if (kind !== 'world' && kind !== 'object' && kind !== 'lesson') return undefined;
  if (typeof title !== 'string' || title.length === 0 || title.length > 200) return undefined;
  return { ticket, kind, title };
}

/** `session.media`, the answer to `device.mediaRefresh`: the media and the 360° photos' variants. */
export type SignedMedia = { media: Record<string, string>; variants: PanoVariants };

/**
 * The API said no to `device.mediaRefresh` (an `error` answering it: no session, or an older API
 * that does not know it). A timeout or a dropped socket is not this: the hub may only have dropped
 * a refresh asked too soon after the last.
 */
export class MediaRefusedError extends Error {
  readonly refused = true;
}

/** How long `refreshMedia` waits for its `session.media`. */
const REFRESH_TIMEOUT_MS = 5_000;

/**
 * Whether a `session.assigned` is the lesson already running (voice-guide
 * design, H4). The hub says the lesson again on every reconnect, so a Wi-Fi
 * blip must not start it over: the same session with the same lesson keeps
 * running and takes only what is new (fresh signatures, cues), while the
 * class's step, guide, sound and pause follow in the `session.state` after it.
 * The message carries no version number, but a session runs one lesson
 * version, so the body stands in for it: a changed body is a new lesson.
 */
export function sameAssignment(
  running: Pick<Assignment, 'sessionId' | 'lesson'> | undefined,
  next: Pick<Assignment, 'sessionId' | 'lesson'>,
): boolean {
  if (running === undefined) return false;
  return (
    running.sessionId === next.sessionId &&
    running.lesson.id === next.lesson.id &&
    JSON.stringify(running.lesson) === JSON.stringify(next.lesson)
  );
}

/** The class as the API sees it; the headset uses only its own part. */
export type LiveState = {
  sessionId?: string;
  stepId?: string;
  control?: 'locked' | 'free';
  paused?: boolean;
  devices?: { id: string; stepId: string; voiceDone?: string }[];
  /** The teacher's two switches (docs/05, ADR-034): absent from an older API. */
  sound?: { ambient?: unknown; voice?: unknown };
  /** The teacher's "Begeleiding: tekst / stem" (ADR-035): absent from an older API. */
  guide?: unknown;
  /** `preparing` until the teacher starts a prepared session (ADR-038): absent from an older API. */
  phase?: unknown;
  /** The teacher's "Spannend" (ADR-043): absent from an older API. */
  spannend?: unknown;
  /** The class clock (ADR-054, 20 Oct 2026; `class-clock.ts`): absent from an older API. */
  clock?: unknown;
};

/** The session's phase from a state message: anything but `preparing`, an older API's absence too, is running. */
export function phaseOfState(state: Pick<LiveState, 'phase'>): SessionPhase {
  return phaseFrom(state.phase);
}

/** The class's guidance from a state message: anything but `'voice'` is text, as an older API's absence is. */
export function guideOfState(state: Pick<LiveState, 'guide'>): Guide {
  return guideOf(state.guide);
}

/**
 * The class's "Spannend" from a state message (ADR-043): on only for exactly `true`; an older
 * API's absence, or anything else, is calm. The engine reads it at step entry.
 */
export function spannendOf(state: Pick<LiveState, 'spannend'>): boolean {
  return state.spannend === true;
}

/** "Achtergrondgeluid" and "Voorlezen" for this class: each on unless the teacher switched it off. */
export type SoundSettings = { ambient: boolean; voice: boolean };

/** The class's switches from a state message; a missing one is on, so an older API still gives sound. */
export function soundOf(state: Pick<LiveState, 'sound'>): SoundSettings {
  return { ambient: state.sound?.ambient !== false, voice: state.sound?.voice !== false };
}

export type OutgoingResult = {
  stepId: string;
  // From the player's own result type rather than repeated, so a new kind
  // (assemble, ADR-025) cannot be recorded by the lesson and then refused by
  // the socket client that is supposed to deliver it.
  kind: LessonResult['kind'];
  payload: Record<string, unknown>;
  /** ISO 8601, from when the student answered. */
  at: string;
  /** The session it belongs to, so a late flush still knows where it goes. */
  sessionId?: string;
  /** Its own id (ResultQueue), so a resend is stored once and the ack finds it. */
  resultId?: string;
};

/**
 * What became of one result sent (`result.ack`, docs/05-session-protocol.md):
 * `stored`; `stored-late`, under the ended or other lesson it named; `dropped`,
 * never to be stored, so not to be sent again; or `kept` -- not answered in
 * time, or not storable just now -- to be sent again on the next flush.
 */
export type ResultOutcome = 'stored' | 'stored-late' | 'dropped' | 'kept';

/**
 * The `error` codes that mean a result will never be stored, from an API that
 * answers a refusal with an error alone. Any other code (`unavailable`, the
 * database away) keeps it: a resend is harmless now, a lost answer is not.
 */
// `bad-payload` assumes the API knows every result kind this headset records, which holds while
// both ship together from one origin; a headset served apart from its API would need it kept.
const REFUSED_FOR_GOOD: readonly unknown[] = [
  'no-session',
  'rejected',
  'forbidden',
  'bad-payload',
  'malformed',
];

type Frame = { type?: string; payload?: Record<string, unknown> };

/** Identifies one result across the send and its acknowledgement, for an API that does not echo its id. */
const keyOf = (result: { stepId: string; at: string }): string => `${result.stepId}|${result.at}`;

/** A sent result waiting for its answer. */
type PendingResult = {
  key: string;
  resultId: string | undefined;
  settle: (outcome: ResultOutcome) => void;
};

export class SessionClient {
  readonly #options: SessionClientOptions;
  readonly #retries: number[];
  readonly #ackTimeoutMs: number;

  #socket: Socket | undefined;
  #running = false;
  #attempt = 0;
  #seq = 0;
  #timer: ReturnType<typeof setTimeout> | undefined;
  /** Sent results waiting for their `result.ack`, in the order they were sent. */
  readonly #pendingResults = new Set<PendingResult>();

  #onAssigned: ((assignment: Assignment) => void) | undefined;
  #onPrepare: ((preparation: Preparation) => void) | undefined;
  #onPreview: ((open: PreviewOpen) => void) | undefined;
  /** Asked-for signatures (`device.mediaRefresh`), waiting for the next `session.media`. */
  readonly #refreshes = new Map<
    string,
    {
      resolve: (signed: SignedMedia) => void;
      reject: (error: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  #refreshCount = 0;

  /**
   * The name the student typed, kept only in this tab's memory.
   *
   * Never written to storage: ADR-021 says a name lives in the session it
   * was said in, and a headset that remembers the last child who wore it
   * across a reload is exactly what that decision rules out.
   */
  #studentName = '';
  #onState: ((state: LiveState) => void) | undefined;
  #onEnded: (() => void) | undefined;
  #onReleased: (() => void) | undefined;
  #onConnected: ((connected: boolean) => void) | undefined;
  #onRevoked: (() => void) | undefined;
  /** Said once: the hub's error and its close are one unlink. */
  #revoked = false;
  /** The session the last `session.assigned` put this headset in. */
  #sessionId: string | undefined;
  /**
   * A `device.voiceDone` said while offline: only the latest, with the
   * session it was heard in. Said again once the server has put this headset
   * back in that session (its `session.assigned` after a reconnect) -- not at
   * the connect, when the hub has no session for it yet -- and dropped when
   * another session is assigned or the lesson ends.
   */
  #voiceDone: { stepId: string; sessionId: string | undefined } | undefined;
  #onFurniture: ((wire: FurnitureWire) => void) | undefined;
  #onMoves: ((moves: MovesWire) => void) | undefined;
  /** This headset's device id, as the furnishing messages name it: session.assigned's, or the check-in's. */
  #deviceId: string | undefined;
  /** The last `session.furniture` of this session, for a lesson that starts listening late. */
  #furniture: FurnitureWire | undefined;
  #grabs = 0;
  /** Grabs waiting for their answer, by the envelope id they were sent with. */
  readonly #pendingGrabs = new Map<
    string,
    {
      pieceId: string;
      resolve: (answer: GrabAnswer) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  constructor(options: SessionClientOptions) {
    this.#options = options;
    this.#retries = options.retryDelaysMs ?? [1_000, 2_000, 5_000, 10_000, 15_000];
    this.#ackTimeoutMs = options.ackTimeoutMs ?? 8_000;
  }

  get connected(): boolean {
    return this.#socket !== undefined;
  }

  onAssigned(handler: (assignment: Assignment) => void): void {
    this.#onAssigned = handler;
  }

  /** `session.prepare` (ADR-038), on enrol and on every reconnect while the session is preparing. */
  onPrepare(handler: (preparation: Preparation) => void): void {
    this.#onPrepare = handler;
  }

  /** `preview.open` (ADR-058): a world or object sent from the console to this headset. */
  onPreview(handler: (open: PreviewOpen) => void): void {
    this.#onPreview = handler;
  }

  onState(handler: (state: LiveState) => void): void {
    this.#onState = handler;
  }

  onEnded(handler: () => void): void {
    this.#onEnded = handler;
  }

  /** The teacher has let this student out of the lesson. */
  onReleased(handler: () => void): void {
    this.#onReleased = handler;
  }

  onConnected(handler: (connected: boolean) => void): void {
    this.#onConnected = handler;
  }

  /**
   * The headset was unlinked from its school (docs/05): the hub's `error` with `device-revoked`, its
   * close with DEVICE_REVOKED_CLOSE, or a ticket refused for it. Said once; the client has stopped
   * by then and does not reconnect -- there is nothing left to connect as.
   */
  onRevoked(handler: () => void): void {
    this.#onRevoked = handler;
  }

  /** `session.furniture`: every piece where it stands and who holds it (ADR-037). */
  onFurniture(handler: (wire: FurnitureWire) => void): void {
    this.#onFurniture = handler;
  }

  /** `session.moves`: pieces other headsets are carrying, batched every 100 ms. */
  onMoves(handler: (moves: MovesWire) => void): void {
    this.#onMoves = handler;
  }

  /**
   * This headset's device id in the furnishing messages: from `session.assigned`, or from the
   * check-in until one says it (`knowDevice`). Never guessed: until it is known, no grab is granted.
   */
  get deviceId(): string | undefined {
    return this.#deviceId;
  }

  /** The check-in's word on who this headset is (`GET /v1/me/device`'s `id`). */
  knowDevice(deviceId: string): void {
    this.#deviceId ??= deviceId;
  }

  /** The last `session.furniture` since this session was assigned, if any. */
  get furniture(): FurnitureWire | undefined {
    return this.#furniture;
  }

  start(): void {
    if (this.#running) return;
    this.#running = true;
    void this.#connect();
  }

  stop(): void {
    this.#running = false;
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = undefined;
    const socket = this.#socket;
    // What waits on it is answered now: an unpair waits for the prefetch, which may wait for a refresh.
    if (socket) this.#dropped();
    socket?.close();
  }

  // --- what the headset tells the server -----------------------------------

  /** The lesson is loaded and this headset is waiting to be steered. */
  ready(lessonVersion?: number): void {
    this.#send('device.ready', lessonVersion === undefined ? {} : { lessonVersion });
  }

  /** The student has put the headset on, or taken it off. */
  presenting(presenting: boolean): void {
    this.#send('device.presenting', { presenting });
  }

  /**
   * Who is wearing this headset, if they said.
   *
   * Remembered here and said again on every connect and every lesson pushed:
   * the server keeps a name only for the life of the session it was said in
   * (ADR-021), so anything that starts a new one -- a reconnect, a teacher
   * pressing start -- has to hear it again or the grid forgets who is who.
   *
   * The name is typed on the waiting screen, which is *before* any session
   * exists, so the first send has nowhere to land by definition. That is the
   * case this remembering exists for, not an edge one.
   */
  named(name: string): void {
    this.#studentName = name;
    this.#send('device.name', { name });
  }

  /**
   * Nobody is wearing it any more.
   *
   * Called when a lesson ends: the headset goes back on a shelf or on to the
   * next child, and the name that was true for that lesson stops being true.
   * Not sent to the server -- the session it belonged to is over, and the
   * server forgets it with the session (ADR-021). This is only about not
   * asserting it into the *next* one on a stranger's behalf.
   */
  forgetName(): void {
    this.#studentName = '';
  }

  /** The student has reached the end and would like to come out. */
  finished(): void {
    this.#send('device.finished', {});
  }

  /**
   * The lesson is torn down and this headset is back at its lobby.
   *
   * Sent after the exit rather than instead of it: the teacher approving a
   * request is permission, and a grid that shows permission as departure
   * tells a teacher a student has gone when the headset is still on a head.
   */
  left(): void {
    this.#send('device.left', {});
  }

  progress(stepId: string): void {
    this.#send('device.progress', { stepId });
  }

  /**
   * This headset has heard a step's voice to its end, or an answer cut it
   * (ADR-035): the grid's "Stem klaar". The player says so once per step
   * entry. Offline it is kept -- only the latest -- and said once the
   * reconnected socket is back in the same session;
   * a stale one names a step the class has left, which the hub drops quietly,
   * and an older API's `unknown-type` error is ignored like any other.
   */
  voiceDone(stepId: string): void {
    if (this.#socket) {
      this.#voiceDone = undefined;
      this.#send('device.voiceDone', { stepId });
    } else {
      this.#voiceDone = { stepId, sessionId: this.#sessionId };
    }
  }

  // --- prefetch (ADR-038) ------------------------------------------------------

  /** How far this headset is with filling its cache: the grid's "42 %" and "Klaar". */
  prefetchProgress(report: PrefetchReport): void {
    this.#send('device.prefetch', { ...report });
  }

  /**
   * The session's media signed again (`device.mediaRefresh`): a prepare outlives the 15-minute
   * signatures. Resolves with the next `session.media`'s `media` and its 360° photos' `variants`
   * (ADR-041; `{}` from an older API); rejects at once offline, after 5 s unanswered, or when the
   * API refuses (an `error` answering it: no session, or an older API).
   */
  refreshMedia(): Promise<SignedMedia> {
    if (!this.#socket) return Promise.reject(new Error('not connected'));
    const id = `r${++this.#refreshCount}`;
    return new Promise<SignedMedia>((resolve, reject) => {
      const timer = setTimeout(
        () => this.#answerRefresh(id, new Error('no fresh media within 5 s')),
        REFRESH_TIMEOUT_MS,
      );
      this.#refreshes.set(id, { resolve, reject, timer });
      this.#send('device.mediaRefresh', {}, undefined, id);
    });
  }

  #answerRefresh(id: string, answer: SignedMedia | Error): void {
    const pending = this.#refreshes.get(id);
    if (!pending) return;
    this.#refreshes.delete(id);
    clearTimeout(pending.timer);
    if (answer instanceof Error) pending.reject(answer);
    else pending.resolve(answer);
  }

  // --- furnishing (ADR-037) ---------------------------------------------------

  /**
   * Asks for a piece. Resolves `'granted'` when a `session.furniture` names this headset as its
   * holder, the refusal's code from an `error` answering this grab, or `'timeout'` after 2 s -- at
   * once when offline. The server decides; the piece does not move until it has.
   */
  grab(pieceId: string): Promise<GrabAnswer> {
    if (!this.#socket) return Promise.resolve('timeout');
    const id = `g${++this.#grabs}`;
    return new Promise<GrabAnswer>((resolve) => {
      const timer = setTimeout(() => this.#answerGrab(id, 'timeout'), FURNISH_RATES.grabTimeoutMs);
      this.#pendingGrabs.set(id, { pieceId, resolve, timer });
      this.#send('device.grab', { pieceId }, undefined, id);
    });
  }

  /** Where a held piece is now; only while connected: a stale position is worth nothing. */
  hold(pose: PosePayload): void {
    this.#send('device.hold', poseFrame(pose));
  }

  /**
   * Lets a piece go where it stands, once. Offline it is not kept: the server ended the hold when
   * the socket closed, and the piece stays where it was last streamed.
   */
  drop(pose: PosePayload): void {
    this.#send('device.drop', poseFrame(pose));
  }

  #answerGrab(id: string, answer: GrabAnswer): void {
    const pending = this.#pendingGrabs.get(id);
    if (!pending) return;
    this.#pendingGrabs.delete(id);
    clearTimeout(pending.timer);
    pending.resolve(answer);
  }

  #cancelGrabs(): void {
    for (const id of [...this.#pendingGrabs.keys()]) this.#answerGrab(id, 'timeout');
  }

  #furnitureArrived(wire: FurnitureWire): void {
    for (const [id, pending] of this.#pendingGrabs) {
      const holder = wire.pieces.find((piece) => piece.id === pending.pieceId)?.heldBy ?? null;
      // Its own only by its id; someone else's grant is followed by this grab's refusal. Without
      // an id nothing is granted (the grab times out): a wrong guess would be for good.
      if (holder !== null && holder === this.#deviceId) this.#answerGrab(id, 'granted');
    }
    this.#furniture = wire;
    this.#onFurniture?.(wire);
  }

  /**
   * Sends results and resolves with what became of each, in order, once the
   * server has answered every one -- or after the ack timeout, the rest `kept`.
   *
   * Only an answer lets a result go: ResultQueue keeps whatever is `kept`, so
   * an answer survives a socket that died between send and receive rather
   * than disappearing with it. Each result is answered on its own, so one the
   * server refuses no longer holds back the rest. Rejects when not connected.
   */
  async sendResults(results: readonly OutgoingResult[]): Promise<ResultOutcome[]> {
    if (!this.#socket) throw new Error('not connected');
    if (results.length === 0) return [];

    const outcomes: (ResultOutcome | undefined)[] = results.map(() => undefined);
    const pending: PendingResult[] = [];
    let allAnswered!: () => void;
    const answered = new Promise<void>((resolve) => (allAnswered = resolve));
    let left = results.length;

    results.forEach((result, index) => {
      const entry: PendingResult = {
        key: keyOf(result),
        resultId: result.resultId?.toLowerCase(),
        settle: (outcome) => {
          if (!this.#pendingResults.delete(entry)) return;
          outcomes[index] = outcome;
          if (--left === 0) allAnswered();
        },
      };
      pending.push(entry);
      this.#pendingResults.add(entry);
    });

    for (const result of results) {
      this.#send(
        'device.result',
        {
          stepId: result.stepId,
          kind: result.kind,
          payload: result.payload,
          at: result.at,
          ...(result.resultId === undefined ? {} : { resultId: result.resultId }),
        },
        result.sessionId,
        // As the envelope's id too, so an `error` about it says which (replyTo).
        result.resultId,
      );
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, this.#ackTimeoutMs);
    });

    try {
      await Promise.race([answered, timeout]);
    } finally {
      clearTimeout(timer);
      for (const entry of pending) this.#pendingResults.delete(entry);
    }
    return outcomes.map((outcome) => outcome ?? 'kept');
  }

  /** The waiting result an ack is about: by its id when it says one, by step and time when not. */
  #pendingResultFor(payload: Record<string, unknown>): PendingResult | undefined {
    const resultId = payload['resultId'];
    if (typeof resultId === 'string') {
      const id = resultId.toLowerCase();
      for (const entry of this.#pendingResults) if (entry.resultId === id) return entry;
      return undefined;
    }
    const stepId = payload['stepId'];
    const at = payload['at'];
    if (typeof stepId !== 'string' || typeof at !== 'string') return undefined;
    const key = keyOf({ stepId, at });
    for (const entry of this.#pendingResults) if (entry.key === key) return entry;
    return undefined;
  }

  // --- the socket ----------------------------------------------------------

  async #connect(): Promise<void> {
    if (!this.#running) return;

    let ticket: string;
    try {
      ticket = await this.#options.ticket();
    } catch (error) {
      if ((error as { revoked?: unknown } | undefined)?.revoked === true) {
        this.#unlinked();
        return;
      }
      // The API is down, not the pairing. Keep trying: giving up would leave
      // the headset dark until someone restarted it.
      this.#retryLater();
      return;
    }

    if (!this.#running) return;

    // Same origin, so the app contains no hostname: the page was served from
    // wherever the API is, and a reverse proxy puts them together.
    const scheme = globalThis.location?.protocol === 'https:' ? 'wss' : 'ws';
    const host = globalThis.location?.host ?? 'localhost';
    const url = `${scheme}://${host}/v1/ws?ticket=${encodeURIComponent(ticket)}`;

    const socket = this.#options.open(url, {
      onOpen: () => {
        // Stopped while it was opening: an unpaired headset keeps no socket.
        if (!this.#running) {
          socket.close();
          return;
        }
        this.#socket = socket;
        this.#attempt = 0;
        this.#seq = 0;
        this.#send('device.hello', { appVersion: this.#options.appVersion ?? 'dev' });
        // The server keeps a name only while the session it was said in runs,
        // so a fresh socket is a server that has never heard it.
        if (this.#studentName.length > 0) this.#send('device.name', { name: this.#studentName });
        this.#onConnected?.(true);
      },
      // Nothing after stop(): a lesson arriving behind an unlink must not start over the pairing screen.
      onMessage: (raw) => {
        if (this.#running) this.#receive(raw);
      },
      onClose: (code) => {
        if (this.#socket === socket) this.#dropped();
        if (code === DEVICE_REVOKED_CLOSE) this.#unlinked();
        this.#retryLater();
      },
    });
  }

  /** The socket in use is gone: what waited on it is answered now, not at its timeout. */
  #dropped(): void {
    this.#socket = undefined;
    // A refresh asked on this socket has no answer coming.
    for (const id of [...this.#refreshes.keys()]) {
      this.#answerRefresh(id, new Error('the connection dropped'));
    }
    // Nor a result sent on it: kept, and sent again once reconnected,
    // rather than holding the queue's flush for the rest of the timeout.
    for (const entry of [...this.#pendingResults]) entry.settle('kept');
    this.#onConnected?.(false);
  }

  /** Unlinked (onRevoked): stopped for good, and said once. */
  #unlinked(): void {
    // The hub's error comes before its close; stop answers what waits on the socket at once.
    this.stop();
    if (this.#revoked) return;
    this.#revoked = true;
    this.#onRevoked?.();
  }

  #retryLater(): void {
    if (!this.#running || this.#timer) return;
    const delay = this.#retries[Math.min(this.#attempt, this.#retries.length - 1)] ?? 15_000;
    this.#attempt += 1;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      void this.#connect();
    }, delay);
  }

  #send(type: string, payload: Record<string, unknown>, sessionId?: string, id?: string): void {
    // Dropped rather than queued when there is no socket: everything except a
    // result is about the moment it was sent, and a result has its own queue.
    this.#socket?.send(
      JSON.stringify({
        type,
        ...(id === undefined ? {} : { id }),
        seq: this.#seq++,
        ...(sessionId === undefined ? {} : { sessionId }),
        payload,
      }),
    );
  }

  /**
   * A lesson the hub put this headset in, `session.assigned` or `session.prepare` alike: this
   * session's id and device id are known from here, and the student's name is said again.
   */
  #enrolled(payload: Record<string, unknown>): Assignment | undefined {
    const lesson = payload['lesson'];
    const sessionId = payload['sessionId'];
    if (!lesson || typeof sessionId !== 'string') return undefined;
    // Another session's pieces are not this one's; the same session's stay until the hub says them again.
    if (sessionId !== this.#sessionId) this.#furniture = undefined;
    // The name is said within the session it was said in, or into the first after none (typed in
    // the lobby): never into another session, whose pupil may be someone else (ADR-021), so it is
    // let go of when the hub moves this headset on without ending the last.
    if (this.#sessionId !== undefined && this.#sessionId !== sessionId) this.#studentName = '';
    this.#sessionId = sessionId;
    // A grab asked of another lesson has no answer coming.
    this.#cancelGrabs();
    if (typeof payload['deviceId'] === 'string') this.#deviceId = payload['deviceId'];
    const furniture = furnitureAssignedOf(payload['furniture']);
    // A name typed on the waiting screen was said before this session
    // existed, so the server had nowhere to keep it and dropped it. Now
    // there is somewhere, and this is the moment to say it again.
    if (this.#studentName.length > 0) this.#send('device.name', { name: this.#studentName });
    return {
      sessionId,
      lesson: lesson as Lesson,
      media: (payload['media'] as Record<string, string>) ?? {},
      cues: cuesFrom(payload['cues']),
      ...(furniture ? { furniture } : {}),
      mediaInfo: mediaInfoOf(payload['mediaInfo']),
      tuning: tuningOf(payload['prefetch']),
      prefetchOffered: payload['prefetch'] !== undefined,
      variants: panoVariantsOf(payload['variants']),
      models: modelCatalogueOf(payload['models']),
      worlds: servedWorldsOf(payload['worlds']),
    };
  }

  #receive(raw: string): void {
    let frame: Frame & { replyTo?: unknown };
    try {
      frame = JSON.parse(raw) as Frame;
    } catch {
      // A frame we cannot read is not worth dropping a lesson over.
      return;
    }

    const payload = frame.payload ?? {};

    switch (frame.type) {
      case 'session.assigned': {
        const assignment = this.#enrolled(payload);
        if (!assignment) return;
        const { sessionId } = assignment;
        const heard = this.#voiceDone;
        this.#voiceDone = undefined;
        this.#onAssigned?.(assignment);
        // Heard while offline: said now that the server has this headset back
        // in the session it was heard in -- after the lesson is handed over, so
        // a step it reports then comes first; heard in another lesson, dropped.
        if (heard !== undefined && heard.sessionId === sessionId) {
          this.#send('device.voiceDone', { stepId: heard.stepId });
        }
        return;
      }
      case 'session.prepare': {
        // As session.assigned, but nothing is built: the lesson is being set ready (ADR-038).
        const preparation = this.#enrolled(payload);
        if (preparation) this.#onPrepare?.(preparation);
        return;
      }
      case 'preview.open': {
        // Its fields are top-level, not in `payload` (docs/05).
        const open = previewOpenOf(frame);
        if (open) this.#onPreview?.(open);
        return;
      }
      case 'session.media': {
        // The answer to the oldest refresh still waiting: the hub answers them in order.
        const [id] = this.#refreshes.keys();
        if (id !== undefined) {
          this.#answerRefresh(id, {
            media: signedMediaOf(payload['media']),
            variants: panoVariantsOf(payload['variants']),
          });
        }
        return;
      }
      case 'session.state':
        this.#onState?.(payload);
        return;
      case 'result.ack': {
        const status = payload['status'];
        // An API from before per-result answers acked only what it stored.
        const outcome: ResultOutcome =
          status === undefined || status === 'stored'
            ? 'stored'
            : status === 'stored-late' || status === 'dropped'
              ? status
              : 'kept';
        this.#pendingResultFor(payload)?.settle(outcome);
        return;
      }
      case 'session.furniture': {
        const wire = furnitureWireOf(payload);
        if (wire) this.#furnitureArrived(wire);
        return;
      }
      case 'session.moves': {
        const pieces = Array.isArray(payload['pieces']) ? payload['pieces'] : [];
        this.#onMoves?.({ pieces: pieces.flatMap((piece) => moveOf(piece) ?? []) });
        return;
      }
      case 'error': {
        // The hub telling this headset it was unlinked: its close follows.
        if (payload['code'] === DEVICE_REVOKED) {
          this.#unlinked();
          return;
        }
        const replyTo = frame.replyTo;
        const result =
          typeof replyTo === 'string'
            ? [...this.#pendingResults].find((entry) => entry.resultId === replyTo.toLowerCase())
            : undefined;
        if (result) {
          result.settle(REFUSED_FOR_GOOD.includes(payload['code']) ? 'dropped' : 'kept');
          return;
        }
        if (typeof replyTo === 'string' && this.#refreshes.has(replyTo)) {
          const code = typeof payload['code'] === 'string' ? payload['code'] : 'refused';
          this.#answerRefresh(
            replyTo,
            new MediaRefusedError(`the media was not signed again: ${code}`),
          );
          return;
        }
        if (typeof replyTo !== 'string' || !this.#pendingGrabs.has(replyTo)) return;
        const code = payload['code'];
        this.#answerGrab(
          replyTo,
          (GRAB_REFUSALS as readonly unknown[]).includes(code)
            ? (code as GrabRefusal)
            : 'no-furniture',
        );
        return;
      }
      case 'session.released':
        this.#cancelGrabs();
        this.#furniture = undefined;
        this.#voiceDone = undefined;
        this.#sessionId = undefined;
        this.#onReleased?.();
        return;
      case 'session.ended':
        this.#cancelGrabs();
        this.#furniture = undefined;
        this.#voiceDone = undefined;
        this.#sessionId = undefined;
        this.#onEnded?.();
        return;
      default:
        // An `error` nobody waits for -- an older API's `unknown-type` for
        // device.voiceDone (ADR-035) -- is not worth anything but silence.
        return;
    }
  }
}

/** `session.media`'s map, entries that are not a string URL left out. */
function signedMediaOf(value: unknown): Record<string, string> {
  const media: Record<string, string> = {};
  if (!isObject(value)) return media;
  for (const [id, url] of Object.entries(value)) if (typeof url === 'string') media[id] = url;
  return media;
}

/** A pose as it goes on the wire: rounded (to the centimetre and the degree), never clamped here. */
function poseFrame(pose: PosePayload): Record<string, unknown> {
  const rounded = roundPose(pose);
  return {
    pieceId: pose.pieceId,
    at: rounded.at,
    turn: rounded.turn,
    material: rounded.material,
    // Only a raised piece (on a table, on a wall) has a height; a pose on the floor goes as it always did.
    ...(rounded.y !== undefined ? { y: rounded.y } : {}),
  };
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isAt = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((n) => typeof n === 'number' && Number.isFinite(n));

function moveOf(value: unknown): MoveWire | undefined {
  if (!isObject(value)) return undefined;
  const { id, at, turn, material, y } = value;
  if (typeof id !== 'string' || !isAt(at) || typeof turn !== 'number' || !Number.isFinite(turn))
    return undefined;
  if (!isMaterial(material)) return undefined;
  // A raised piece (on a table, on a wall): as the hub's parsePose, 0 to 20 m when present.
  if (y !== undefined && !(typeof y === 'number' && Number.isFinite(y) && y >= 0 && y <= 20))
    return undefined;
  return { id, at: [at[0], at[1]], turn, material, ...(y ? { y } : {}) };
}

function pieceWireOf(value: unknown): PieceWire | undefined {
  const move = moveOf(value);
  if (!move || !isObject(value)) return undefined;
  const heldBy = value['heldBy'];
  if (heldBy !== null && typeof heldBy !== 'string') return undefined;
  return { ...move, heldBy };
}

/** A `session.furniture` payload as the seam gives it, or undefined; a malformed piece is left out. */
export function furnitureWireOf(payload: unknown): FurnitureWire | undefined {
  if (!isObject(payload)) return undefined;
  const { locked, rev, pieces } = payload;
  if (typeof locked !== 'boolean' || typeof rev !== 'number' || !Array.isArray(pieces))
    return undefined;
  return { locked, rev, pieces: pieces.flatMap((piece) => pieceWireOf(piece) ?? []) };
}

function pieceInfoOf(value: unknown): PieceInfo | undefined {
  if (!isObject(value)) return undefined;
  const { id, title, media, size } = value;
  if (typeof id !== 'string' || typeof title !== 'string') return undefined;
  if (typeof media !== 'string' || !media.startsWith('m_')) return undefined;
  if (
    !Array.isArray(size) ||
    size.length !== 3 ||
    !size.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)
  )
    return undefined;
  return {
    id,
    title,
    media: media as `m_${string}`,
    size: [size[0] as number, size[1] as number, size[2] as number],
  };
}

/** `session.assigned`'s `furniture`, pieces that are not the seam's shape left out. */
export function furnitureAssignedOf(value: unknown): FurnitureAssigned | undefined {
  if (!isObject(value) || typeof value['setTitle'] !== 'string') return undefined;
  const pieces = Array.isArray(value['pieces']) ? value['pieces'] : [];
  return {
    setTitle: value['setTitle'],
    pieces: pieces.flatMap((piece) => pieceInfoOf(piece) ?? []),
  };
}

/** Opens a real browser WebSocket, adapted to the interface above. */
export function browserSocket(url: string, handlers: SocketHandlers): Socket {
  const socket = new WebSocket(url);
  socket.addEventListener('open', () => handlers.onOpen());
  socket.addEventListener('message', (event) => handlers.onMessage(String(event.data)));
  // Both, because a failed handshake fires error and then close; onClose is
  // written to be safe to call more than once.
  socket.addEventListener('close', (event) => handlers.onClose(event.code));

  return {
    send: (data) => socket.send(data),
    close: () => socket.close(),
  };
}

/**
 * Trades the device token for a single-use ticket (docs/05). Unlinked:
 * deviceFetch unpairs the headset and this rejects with DeviceRevokedError.
 */
export async function fetchTicket(identity: DeviceIdentity): Promise<string> {
  const response = await deviceFetch(identity, '/v1/ws-tickets', { method: 'POST' });
  if (!response.ok) throw new Error(`could not get a ticket (${response.status})`);
  const body = (await response.json()) as { ticket?: string };
  if (!body.ticket) throw new Error('the server returned no ticket');
  return body.ticket;
}
